import { Autocomplete, Stack, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { type FlowTagCategory } from 'flow-schema';
import { type FieldInputProps } from 'formik';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';
import uniqBy from 'lodash/uniqBy';

import { Text, api, getQueryProps, isDefined, isTruthy } from 'common';

import { sortFlowTemplateTagNames } from '../../utils';

interface TagOption {
  id: string;
  name: string;
  originalName: string;
  category: string | null;
}

export interface TagAutocompleteCommonProps {
  category: FlowTagCategory;
  disabled?: boolean;
  placeholder?: string;
  optionDescriptions?: Record<string, string>;
}

interface SingleSelectProps extends TagAutocompleteCommonProps {
  isFreeSolo: boolean;
  isMultiple: false;
  fieldId: string;
  field: FieldInputProps<string>;
}

interface MultiSelectProps extends TagAutocompleteCommonProps {
  isFreeSolo: boolean;
  isMultiple: true;
  fieldId: string;
  field: FieldInputProps<string[]>;
}

export function TagAutocompleteInner(props: SingleSelectProps | MultiSelectProps) {
  const {
    category,
    disabled,
    isFreeSolo,
    isMultiple,
    field,
    fieldId,
    placeholder,
    optionDescriptions,
  } = props;

  const { data: options = [], isPending } = useQuery({
    ...getQueryProps(api.client.Tag, 'findAll', { category }),
    select: (rawTags): TagOption[] => {
      return uniqBy([...(rawTags ?? [])], 'name')
        .map((tag) => ({
          id: tag.name,
          name: startCase(tag.name),
          originalName: tag.name,
          category: tag.category,
        }))
        .sort((a, b) => sortFlowTemplateTagNames(a.name, b.name));
    },
  });

  return (
    <Autocomplete<TagOption | string, boolean, false, boolean>
      multiple={isMultiple}
      freeSolo={isFreeSolo}
      id={fieldId}
      disabled={disabled}
      loading={isPending}
      options={options}
      clearOnBlur
      renderOption={(props, option) => {
        if (typeof option === 'string') {
          return <li {...props} key={option}>{`Add "${option}"`}</li>;
        }
        const description = optionDescriptions?.[option.originalName];
        return (
          <li {...props} key={option.id}>
            {isDefined(description) ? (
              <Stack direction="column" gap={0.25}>
                <Text component="span" variant="body2" bold>
                  {option.name}
                </Text>
                <Text
                  component="span"
                  variant="body2"
                  sx={{ color: 'text.secondary', fontSize: 13 }}
                >
                  {description}
                </Text>
              </Stack>
            ) : (
              option.name
            )}
          </li>
        );
      }}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option;
        }
        return option.name;
      }}
      value={
        isFreeSolo
          ? isMultiple
            ? field.value || []
            : field.value
          : isMultiple
            ? options.filter(
                (option) => Array.isArray(field.value) && field.value.includes(option.originalName)
              )
            : (options.find((option) => option.originalName === field.value) ?? null)
      }
      onChange={(_, newValue) => {
        if (isMultiple) {
          if (Array.isArray(newValue)) {
            const value = new Set(
              newValue.map((tag) => (typeof tag === 'object' ? tag.originalName : camelCase(tag)))
            );
            field.onChange({ target: { value: Array.from(value), name: field.name } });
          } else {
            field.onChange({ target: { value: [], name: field.name } });
          }
        } else if (!Array.isArray(newValue)) {
          const value = isDefined(newValue)
            ? typeof newValue === 'object'
              ? newValue.originalName
              : camelCase(newValue)
            : null;
          field.onChange({ target: { value, name: field.name } });
        }
      }}
      filterOptions={(options, { inputValue }) => {
        const trimmedInputValue = inputValue.trim();
        const inputValueMatchesExistingValue = isMultiple
          ? field.value?.some((value) => camelCase(trimmedInputValue) === value)
          : field.value?.toLowerCase() === trimmedInputValue.toLowerCase();
        const filtered = options.filter((option) => {
          if (inputValueMatchesExistingValue) {
            return false;
          }
          const optionMatchesExistingValue = isMultiple
            ? field.value?.some((value) =>
                typeof option === 'string' ? value === option : option.originalName === value
              )
            : typeof option === 'string'
              ? option === field.value
              : option.originalName === field.value;
          if (optionMatchesExistingValue) {
            return false;
          }
          if (typeof option === 'string') {
            return option.toLowerCase().includes(trimmedInputValue.toLowerCase());
          }
          const camelCaseInputValue = camelCase(trimmedInputValue);
          return (
            option.originalName.includes(camelCaseInputValue) ||
            option.name.toLowerCase().includes(trimmedInputValue.toLowerCase())
          );
        });
        if (isTruthy(trimmedInputValue)) {
          const inputValueMatchesExistingOption = options.some((option) => {
            if (typeof option === 'string') {
              return option.toLowerCase() === trimmedInputValue.toLowerCase();
            }
            const camelCaseInputValue = camelCase(trimmedInputValue);
            return option.originalName === camelCaseInputValue;
          });
          if (isFreeSolo && !inputValueMatchesExistingOption && !inputValueMatchesExistingValue) {
            filtered.push(trimmedInputValue);
          }
        }
        return filtered;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={
            isMultiple && Array.isArray(field.value) && isTruthy(field.value?.length)
              ? undefined
              : isTruthy(field.value)
                ? undefined
                : placeholder
          }
          size="small"
        />
      )}
    />
  );
}
